/**
 * Shared listbox behavior for every game-form combobox (status, hardware/platform, tags), so the
 * form behaves as one system: same list, toggle, focus handling, arrow/Enter/Escape keys, active
 * option and empty state. Each field owns only its option list and what a selection means.
 *
 * The query is what the user typed since the list opened, not the input value: a field whose value
 * is already filled (status label, chosen hardware) opens on the full list and narrows on typing.
 * The first option becomes active only for a non-empty query, so Enter on an untouched field picks
 * nothing, while typing a few letters and pressing Enter picks the first match.
 *
 * `filterOnly` is for a field whose text is only ever a filter over a fixed choice (status, a delete
 * target): opening the list empties the field, the current choice stays visible as the placeholder,
 * and the first printable key typed while the list is closed (focus left in the field after a pick)
 * starts a fresh filter instead of extending the chosen name. Backspace/Delete keep native editing;
 * that opening keeps the edited text and shows the current option's label as the placeholder.
 * The field's `onClose` shows the chosen name again. Off, the field keeps its text while filtering.
 *
 * Focus, a click/tap on a closed field, the toggle, ArrowDown/ArrowUp and typing open the list; a
 * click on an open field leaves the filter, active option and scroll as they are. Every opening
 * reveals the whole list in the window at once (the page's `scroll-padding` clears the fixed
 * navigation), then the current option inside the list. An arrow that opens the list makes the
 * current option active and moves no further, so Enter keeps the choice; with no current option
 * ArrowDown takes the first and ArrowUp the last. The toggle never takes focus.
 *
 * @typedef {{type: 'option'|'create'|'empty', value: string, label: string, tone?: string|null}} ComboboxOption
 *   `tone` present (even null) renders a status dot; null is the neutral dot.
 *
 * @param {{
 *   root: HTMLElement,
 *   input: HTMLInputElement,
 *   toggle: HTMLButtonElement,
 *   list: HTMLUListElement,
 *   buildOptions: (query: string) => ComboboxOption[],
 *   onSelect: (option: ComboboxOption) => void,
 *   onClose?: () => void,
 *   isCurrent?: (option: ComboboxOption) => boolean,
 *   filterOnly?: boolean,
 * }} config
 *   `isCurrent` marks the field's present value with a check, apart from the keyboard-active option.
 * @returns {{isOpen: () => boolean, close: () => void}}
 */
export function initGameFormCombobox({
    root,
    input,
    toggle,
    list,
    buildOptions,
    onSelect,
    onClose,
    isCurrent = () => false,
    filterOnly = false,
}) {
    /** @type {ComboboxOption[]} */
    let options = [];
    let activeIndex = -1;
    let isOpen = false;
    let query = '';
    /** The field's own placeholder while the current choice stands in for it; null when not swapped. */
    let restPlaceholder = null;

    const isSelectable = (index) => index >= 0 && index < options.length && options[index].type !== 'empty';
    const selectableIndexes = () => options.flatMap((option, index) => (option.type === 'empty' ? [] : [index]));

    const syncActiveState = () => {
        list.querySelectorAll('[data-game-form-combobox-option-index]').forEach((element) => {
            element.setAttribute('aria-selected', String(Number(element.dataset.gameFormComboboxOptionIndex) === activeIndex));
        });

        if (isSelectable(activeIndex)) {
            input.setAttribute('aria-activedescendant', `${list.id}-option-${activeIndex}`);
        } else {
            input.removeAttribute('aria-activedescendant');
        }
    };

    const render = () => {
        options = buildOptions(query);
        const selectable = selectableIndexes();
        activeIndex = query !== '' && selectable.length > 0 ? selectable[0] : -1;

        const fragment = document.createDocumentFragment();
        options.forEach((option, index) => {
            const item = document.createElement('li');
            item.className = 'game-form-combobox-option-item';

            if (option.type === 'empty') {
                const empty = document.createElement('p');
                empty.className = 'game-form-combobox-option-empty';
                empty.textContent = option.label;
                item.appendChild(empty);
                fragment.appendChild(item);
                return;
            }

            const button = document.createElement('button');
            button.type = 'button';
            button.tabIndex = -1;
            button.id = `${list.id}-option-${index}`;
            button.className = option.type === 'create'
                ? 'game-form-combobox-option game-form-combobox-option-create'
                : 'game-form-combobox-option';
            button.dataset.gameFormComboboxOptionIndex = String(index);
            button.setAttribute('role', 'option');

            if ('tone' in option) {
                button.appendChild(createStatusDot(option.tone ?? null));
            }

            const label = document.createElement('span');
            label.className = 'game-form-combobox-option-label';
            label.textContent = option.label;
            button.appendChild(label);

            if (option.type === 'option' && isCurrent(option)) {
                button.classList.add('is-current');
                const check = document.createElement('span');
                check.className = 'game-form-combobox-option-check';
                check.setAttribute('aria-hidden', 'true');
                check.textContent = '✓';
                button.appendChild(check);
            }

            item.appendChild(button);
            fragment.appendChild(item);
        });

        list.replaceChildren(fragment);
        list.hidden = !isOpen;
        syncActiveState();
    };

    /** A closed -> open transition shows the whole list; no smooth scroll, the list appears in place. */
    const reveal = () => list.scrollIntoView({ block: 'nearest', behavior: 'instant' });

    const open = () => {
        const wasOpen = isOpen;
        if (!isOpen) {
            isOpen = true;
            query = '';
            input.setAttribute('aria-expanded', 'true');
            if (filterOnly) {
                restPlaceholder = input.placeholder;
                if (input.value !== '') {
                    input.placeholder = input.value;
                }
                input.value = '';
            }
        }
        render();
        if (!wasOpen) {
            reveal();
        }
        list.querySelector('.is-current')?.scrollIntoView({ block: 'nearest' });
    };

    const close = () => {
        if (!isOpen) {
            return;
        }

        isOpen = false;
        query = '';
        activeIndex = -1;
        list.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
        if (restPlaceholder !== null) {
            input.placeholder = restPlaceholder;
            restPlaceholder = null;
        }
        onClose?.();
    };

    const select = (index) => {
        if (!isSelectable(index)) {
            return;
        }

        const option = options[index];
        close();
        onSelect(option);
    };

    const move = (step) => {
        const selectable = selectableIndexes();
        if (selectable.length === 0) {
            return;
        }

        const position = selectable.indexOf(activeIndex);
        const next = position === -1
            ? (step > 0 ? 0 : selectable.length - 1)
            : (position + step + selectable.length) % selectable.length;
        activeIndex = selectable[next];
        syncActiveState();

        list.querySelector(`[data-game-form-combobox-option-index="${activeIndex}"]`)?.scrollIntoView({ block: 'nearest' });
    };

    input.setAttribute('autocomplete', 'off');

    input.addEventListener('focus', open);

    // Focus already in the field (after Escape or a pick) fires no focus event, so a click reopens it.
    input.addEventListener('click', () => {
        if (!isOpen) {
            open();
        }
    });

    input.addEventListener('input', () => {
        const wasOpen = isOpen;
        if (!isOpen) {
            isOpen = true;
            input.setAttribute('aria-expanded', 'true');
            // Native editing (Backspace/Delete) opens here; the edited text is no longer the choice.
            if (filterOnly) {
                restPlaceholder = input.placeholder;
                input.placeholder = buildOptions('').find((option) => option.type === 'option' && isCurrent(option))?.label
                    ?? restPlaceholder;
            }
        }
        query = input.value.replace(/\s+/g, ' ').trim();
        render();
        if (!wasOpen) {
            reveal();
        }
    });

    input.addEventListener('keydown', (event) => {
        // Opening empties the field before the browser inserts the key, so the key alone is the filter.
        if (filterOnly && !isOpen && isPrintableKey(event)) {
            open();
            return;
        }

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (!isOpen) {
                open();
                const current = options.findIndex((option) => option.type === 'option' && isCurrent(option));
                if (current !== -1) {
                    activeIndex = current;
                    syncActiveState();
                    return;
                }
            }
            move(event.key === 'ArrowDown' ? 1 : -1);
            return;
        }

        // An open list owns Enter: it picks the active option or just closes, never submits the form.
        if (event.key === 'Enter' && isOpen) {
            event.preventDefault();
            if (isSelectable(activeIndex)) {
                select(activeIndex);
            } else {
                close();
            }
            return;
        }

        if (event.key === 'Escape' && isOpen) {
            event.preventDefault();
            close();
            return;
        }

        if (event.key === 'Tab') {
            close();
        }
    });

    // A press on the toggle would focus it (closing then left focus there); focus stays in the field.
    toggle.addEventListener('mousedown', (event) => event.preventDefault());

    toggle.addEventListener('click', () => {
        if (isOpen) {
            close();
            return;
        }

        input.focus();
        open();
    });

    // Keeps focus in the input while an option is clicked, so focusout does not close the list first.
    list.addEventListener('mousedown', (event) => event.preventDefault());

    list.addEventListener('click', (event) => {
        const button = event.target instanceof Element
            ? event.target.closest('[data-game-form-combobox-option-index]')
            : null;
        if (!(button instanceof HTMLElement)) {
            return;
        }

        select(Number(button.dataset.gameFormComboboxOptionIndex));
        input.focus();
    });

    root.addEventListener('focusout', (event) => {
        if (event.relatedTarget instanceof Node && root.contains(event.relatedTarget)) {
            return;
        }
        close();
    });

    return { isOpen: () => isOpen, close };
}

/** A key that inserts a character; a composing IME (or one reporting 229) is left alone. */
const isPrintableKey = (event) => event.key.length === 1
    && !event.ctrlKey
    && !event.metaKey
    && !event.altKey
    && !event.isComposing
    && event.keyCode !== 229;

/**
 * A modal backdrop press while the combobox list is open closes only the list; the dialog, its value
 * and the focus stay, and the next press closes the modal. The open state is read when the press
 * starts, because the input's focusout would otherwise close the list before the click arrives;
 * holding the mousedown default keeps focus in the input, so that focusout does not happen.
 *
 * @param {HTMLElement} backdrop
 * @param {{isOpen: () => boolean, close: () => void}} combobox
 * @returns {() => boolean} called by the backdrop's click handler: true when the click only closed the list
 */
export function closeListBeforeBackdrop(backdrop, combobox) {
    let openAtPress = false;
    backdrop.addEventListener('pointerdown', () => {
        openAtPress = combobox.isOpen();
    });
    backdrop.addEventListener('mousedown', (event) => {
        openAtPress = openAtPress || combobox.isOpen();
        if (openAtPress) {
            event.preventDefault();
        }
    });

    return () => {
        const listWasOpen = openAtPress || combobox.isOpen();
        openAtPress = false;
        combobox.close();

        return listWasOpen;
    };
}

/**
 * @param {string|null} tone status tone index from `status-tone-styles.php`; null = neutral dot
 */
export function createStatusDot(tone) {
    const dot = document.createElement('span');
    dot.className = 'game-form-status-dot';
    dot.setAttribute('aria-hidden', 'true');
    if (tone !== null) {
        dot.dataset.statusTone = tone;
    }

    return dot;
}

/** Case-insensitive key shared by matching and duplicate checks in every combobox. */
export const normalizeComboboxName = (value) => value.replace(/\s+/g, ' ').trim();
export const comboboxKey = (value) => normalizeComboboxName(value).toLowerCase();

/**
 * Options for a creatable combobox: known names matching the query case-insensitively, minus the
 * already-chosen ones, then `Create "…"` when the query names nothing known or chosen. Case-only
 * variants never offer a create, so the dictionary cannot grow `JRPG` next to `jrpg`.
 *
 * @param {{query: string, names: string[], chosen?: string[], emptyLabel: string, takenLabel?: string}} config
 * @returns {ComboboxOption[]}
 */
export function buildCreatableOptions({ query, names, chosen = [], emptyLabel, takenLabel = emptyLabel }) {
    const queryKey = comboboxKey(query);
    const chosenKeys = new Set(chosen.map(comboboxKey));
    const options = names
        .filter((name) => !chosenKeys.has(comboboxKey(name)) && comboboxKey(name).includes(queryKey))
        .map((name) => ({ type: 'option', value: name, label: name }));

    const isKnown = chosenKeys.has(queryKey) || names.some((name) => comboboxKey(name) === queryKey);
    if (query !== '' && !isKnown) {
        options.push({ type: 'create', value: query, label: `Create "${query}"` });
    }

    if (options.length === 0) {
        options.push({ type: 'empty', value: '', label: query === '' ? emptyLabel : takenLabel });
    }

    return options;
}

/**
 * @param {string} rawJson JSON array of names from a `data-*` attribute
 * @returns {string[]} trimmed names, first spelling wins on case-insensitive duplicates
 */
export function parseComboboxNames(rawJson) {
    let names = [];
    try {
        names = JSON.parse(rawJson);
    } catch {
        return [];
    }

    const seen = new Set();

    return (Array.isArray(names) ? names : []).flatMap((name) => {
        const normalized = typeof name === 'string' ? normalizeComboboxName(name) : '';
        const key = normalized.toLowerCase();
        if (normalized === '' || seen.has(key)) {
            return [];
        }
        seen.add(key);

        return [normalized];
    });
}
